import React, { useRef, useCallback } from 'react';
import { Group, Rect, Text, Circle } from 'react-konva';

// Offset in screen pixels to raise the item above the finger on touch
const TOUCH_OFFSET_PX = 50;

function FloorPlanFurniture({ item, isSelected, onSelect, onDragEnd, onDragMove, zoom, activeTool }) {
  const handleSize = Math.max(4, 6 / zoom);
  const isSelectTool = activeTool === 'select';
  const isTouchRef = useRef(false);

  const touchOffsetWorld = TOUCH_OFFSET_PX / zoom;

  const handleTouchStart = useCallback(() => {
    isTouchRef.current = true;
  }, []);

  const handleMouseDown = useCallback(() => {
    isTouchRef.current = false;
  }, []);

  const handleDragMove = useCallback((e) => {
    if (isTouchRef.current) {
      const node = e.target;
      node.y(node.y() - touchOffsetWorld);
    }
    if (onDragMove) onDragMove(item.id, e);
  }, [touchOffsetWorld, onDragMove, item.id]);

  const handleDragEnd = useCallback((e) => {
    onDragEnd(item.id, {
      x: e.target.x(),
      y: e.target.y(),
    });
    isTouchRef.current = false;
  }, [onDragEnd, item.id]);

  return (
    <Group
      x={item.x}
      y={item.y}
      rotation={item.rotation || 0}
      draggable={isSelectTool}
      onTouchStart={handleTouchStart}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        if (!isSelectTool) return;
        e.cancelBubble = true;
        onSelect(item.id);
      }}
      onTap={(e) => {
        if (!isSelectTool) return;
        e.cancelBubble = true;
        onSelect(item.id);
      }}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      {/* Furniture shape */}
      <Rect
        x={-item.width / 2}
        y={-item.depth / 2}
        width={item.width}
        height={item.depth}
        fill={item.color || '#DEB887'}
        stroke={isSelected ? '#C4612E' : '#999'}
        strokeWidth={isSelected ? 2 / zoom : 1 / zoom}
        cornerRadius={2}
        opacity={0.85}
      />

      {/* Label */}
      <Text
        text={item.label || item.assetId}
        x={-item.width / 2}
        y={-item.depth / 2}
        width={item.width}
        height={item.depth}
        align="center"
        verticalAlign="middle"
        fontSize={Math.min(Math.max(8, 10 / zoom), item.width / 4, item.depth / 3)}
        fill="#333"
        wrap="word"
        padding={2}
      />

      {/* Rotation handle */}
      {isSelected && (
        <>
          {/* Rotation handle line */}
          <Circle
            x={0}
            y={-item.depth / 2 - 16 / zoom}
            radius={handleSize}
            fill="#C4612E"
            stroke="#fff"
            strokeWidth={1.5 / zoom}
            style={{ cursor: 'crosshair' }}
          />

          {/* Selection border */}
          <Rect
            x={-item.width / 2 - 3 / zoom}
            y={-item.depth / 2 - 3 / zoom}
            width={item.width + 6 / zoom}
            height={item.depth + 6 / zoom}
            stroke="#C4612E"
            strokeWidth={1 / zoom}
            dash={[4 / zoom, 4 / zoom]}
            fill="transparent"
          />
        </>
      )}
    </Group>
  );
}

export default React.memo(FloorPlanFurniture);
